import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';

import { apiRequest } from '@/src/services';
import { Label, Loading, ErrorMessage } from '@/src/ui';
import { Constants } from '@/src/utils';

interface ReportingBand {
    id: number;
    examTypeMasterId: number;
    examTypeMasterName: string;
    bandName: string;
    fromPercentage: number;
    toPercentage: number;
    isConsideredPassing: boolean;
}

interface GroupedBands {
    name: string;
    bands: ReportingBand[];
}

interface ReportScoringBandsLegendProps {
    dataLoaded?: boolean;
    showLegend: boolean;
}

const ReportScoringBandsLegend: React.FC<ReportScoringBandsLegendProps> = ({
    showLegend,
    dataLoaded = true,
}) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [groupedBands, setGroupedBands] = useState<GroupedBands[] | null>(null);

    useEffect(() => {
        (async () => {
            try {
                const response = await apiRequest<ReportingBand[]>('get', '/examReportingBands');
                const grouped = response.data.reduce<Record<number, GroupedBands>>((acc, band) => {
                    if (!acc[band.examTypeMasterId]) {
                        acc[band.examTypeMasterId] = { name: band.examTypeMasterName, bands: [] };
                    }
                    acc[band.examTypeMasterId].bands.push(band);
                    return acc;
                }, {});
                setGroupedBands(Object.values(grouped));
            } catch {
                setError('Failed to load Class Matrix Report');
                setGroupedBands(null);
            } finally {
                setLoaded(true);
            }
        })();
    }, []);

    if (!dataLoaded || !showLegend) return null;

    return (
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
            {showLegend && (
                <Box
                    sx={{
                        top: '100%',
                        left: 0,
                        zIndex: 10,
                        mt: 1,
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: '#fff',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                        fontSize: 14,
                        color: '#555',
                        width: 'fit-content',
                        maxWidth: 700,
                    }}
                    aria-label="Status legend"
                >
                    {!loaded ? (
                        <Loading />
                    ) : error ? (
                        <ErrorMessage error={error} />
                    ) : (
                        <>
                            {(groupedBands ?? []).map((group) => (
                                <Box key={group.name} sx={{ mb: 1.5 }}>
                                    <Box
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 13,
                                            color: '#333',
                                            mb: 0.75,
                                        }}
                                    >
                                        {group.name}
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(4, auto)',
                                            columnGap: 2,
                                            rowGap: 0.75,
                                            justifyContent: 'start',
                                        }}
                                    >
                                        {group.bands.map((band) => {
                                            return (
                                                <Box
                                                    key={band.id}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.75,
                                                        fontSize: 13,
                                                        color: '#444',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <Box
                                                        key={band.id}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.75,
                                                            fontSize: 13,
                                                            color: '#444',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        <Label
                                                            color={
                                                                Constants.bandColors[
                                                                    band.bandName
                                                                ] ?? '#888'
                                                            }
                                                        >
                                                            {band.bandName}
                                                        </Label>
                                                        <Box
                                                            component="span"
                                                            sx={{ color: '#888', fontSize: 12 }}
                                                        >
                                                            {band.fromPercentage}–
                                                            {band.toPercentage}%
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            ))}
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ReportScoringBandsLegend;
